import { useState, useCallback } from 'react';

interface ICopyResult
{
	success: boolean;
	error?: string;
}

interface IUseCopyToClipboardReturn
{
	copyToClipboard: (text: string) => Promise<ICopyResult>;
	isCopying: boolean;
	lastCopyResult: ICopyResult | null;
}

/**
 * Hook for copying text to clipboard with modern Clipboard API and fallback
 */
export const useCopyToClipboard = (): IUseCopyToClipboardReturn =>
{
	const [isCopying, setIsCopying] = useState(false);
	const [lastCopyResult, setLastCopyResult] = useState<ICopyResult | null>(null);

	const copyToClipboard = useCallback(async (text: string): Promise<ICopyResult> =>
	{
		setIsCopying(true);
		
		try
		{
			// Try modern Clipboard API first
			if (navigator.clipboard && window.isSecureContext)
			{
				await navigator.clipboard.writeText(text);
				const result = { success: true };
				setLastCopyResult(result);
				setIsCopying(false);
				return result;
			}

			// Fallback for older browsers or non-secure contexts
			const textArea = document.createElement('textarea');
			textArea.value = text;
			textArea.style.position = 'absolute';
			textArea.style.left = '-999999px';
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			
			const successful = document.execCommand('copy');
			document.body.removeChild(textArea);
			
			if (successful)
			{
				const result = { success: true };
				setLastCopyResult(result);
				setIsCopying(false);
				return result;
			}
			else
			{
				throw new Error('Copy command failed');
			}
		}
		catch (error)
		{
			const result = { 
				success: false, 
				error: error instanceof Error ? error.message : 'Copy failed' 
			};
			setLastCopyResult(result);
			setIsCopying(false);
			return result;
		}
	}, []);

	return {
		copyToClipboard,
		isCopying,
		lastCopyResult
	};
};
